<template>
  <div class="order-summary-viewer">
    <div
      v-if="loading"
      class="text-center pa-4"
    >
      <v-progress-circular
        indeterminate
        color="primary"
      />
      <div>{{ t('Loading PDF preview...') }}</div>
    </div>
    <div
      v-else-if="error"
      class="text-center pa-4 text-error"
    >
      {{ t('Failed to load order summary.') }}
    </div>
    <div
      v-else-if="pdfUrl"
      class="pdf-container"
    >
      <PdfViewer
        :src="pdfUrl"
        :key="orderId"
        :initial-page="1"
        :show-toolbar="true"
        :theme="'light'"
        :min-scale="0.5"
        :max-scale="3.0"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import {ref, onBeforeUnmount, watch} from 'vue';
import { useOrdersStore } from '@/stores/orders';
import { SummaryPayload } from "@/entities/order";
import { useI18n } from 'vue-i18n';
import NotificationMessageService from "@/services/notificationmessage.service";
import PdfViewer from "@/components/PdfViewer.vue";

defineOptions({
  name: "OrderSummaryViewer",
});

interface Props {
  orderId: number;
  doctorId?: string;
  languageId?: string;
}

const { t } = useI18n();

const props = withDefaults(defineProps<Props>(), {
  languageId: 'en',
  doctorId: '',
});

const ordersStore = useOrdersStore();
const loading = ref(false);
const error = ref(false);
const pdfUrl = ref<string | null>(null);

let objectUrl: string | null = null;

watch(
  () => [props.orderId, props.doctorId, props.languageId],
  loadPdf,
  { immediate: true }
);

function revokeObjectUrl() {
  if (objectUrl) {
    URL.revokeObjectURL(objectUrl);
    objectUrl = null;
  }
}

async function loadPdf() {
  loading.value = true;
  error.value = false;
  try {
    const payload: SummaryPayload = {
      orderId: props.orderId,
      doctorId: props.doctorId,
      languageId: props.languageId,
    }

    const blob = await ordersStore.fetchOrderSummaryBlob(payload);

    revokeObjectUrl();
    objectUrl = URL.createObjectURL(blob);
    pdfUrl.value = objectUrl;
  } catch (err: unknown) {
    const message = err instanceof Error ? err.message : t('Failed to load order summary.');

    NotificationMessageService.showError(message, t('Error'), 5000);
    console.error('Failed to load order summary.', err);
    error.value = true;
  } finally {
    loading.value = false;
  }
}

onBeforeUnmount(() => {
  revokeObjectUrl();
});
</script>

<style lang="scss" scoped>
.pdf-container {
  margin-top: 1rem;
  border: 1px solid rgb(var(--v-theme-grey-stroke));
  border-radius: 4px;
}
</style>
